import { useEffect, useState } from 'react';
import type { ExecutionMode, Workspace, WorkspaceStatus } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const MODES: { value: ExecutionMode; label: string; desc: string }[] = [
  { value: 'draft', label: 'Draft', desc: 'Always require review' },
  { value: 'assist', label: 'Assist', desc: 'Advisory. Human acts' },
  { value: 'auto', label: 'Auto', desc: 'Publish and log' },
];

function nextStatuses(status: WorkspaceStatus): WorkspaceStatus[] {
  switch (status) {
    case 'draft':
      return ['live', 'archived'];
    case 'live':
      return ['draft', 'archived'];
    case 'archived':
      return [];
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function actionLabel(status: WorkspaceStatus): string {
  switch (status) {
    case 'live':
      return 'Go live';
    case 'draft':
      return 'Move to draft';
    case 'archived':
      return 'Archive';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function statusVariant(status: WorkspaceStatus): 'success' | 'warning' | 'secondary' {
  switch (status) {
    case 'live':
      return 'success';
    case 'archived':
      return 'secondary';
    case 'draft':
      return 'warning';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

export function WorkspacesPage() {
  const { workspaceId, setWorkspace } = useApp();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [mode, setMode] = useState<ExecutionMode>('assist');

  async function load() {
    try {
      setWorkspaces(await apiClient.listWorkspaces());
    } catch (err) {
      setError(errText(err, 'Failed to load workspaces'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function create() {
    setError(null);
    try {
      await apiClient.createWorkspace({ name, slug, defaultExecutionMode: mode });
      setName('');
      setSlug('');
      setMode('assist');
      setSlugEdited(false);
      setCreating(false);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to create workspace'));
    }
  }

  async function setStatus(workspace: Workspace, status: WorkspaceStatus) {
    try {
      await apiClient.setWorkspaceStatus(workspace.id, status);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to update workspace'));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Workspaces</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The unit operators run on. A tenant holds many workspaces.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating((v) => !v)}>
          New workspace
        </Button>
      </div>

      {creating ? (
        <Card className="mt-4">
          <CardContent className="grid max-w-xl gap-3 pt-6">
            <Input
              placeholder="Name (e.g. Customer Support)"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugEdited) setSlug(slugify(e.target.value));
              }}
            />
            <Input
              placeholder="slug (kebab-case, e.g. customer-support)"
              value={slug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value);
              }}
            />
            <div>
              <div className="mb-1.5 text-sm">Default execution mode</div>
              <div className="grid gap-2 sm:grid-cols-3">
                {MODES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setMode(option.value)}
                    className={`rounded-lg border px-3 py-2 text-left text-sm ${
                      mode === option.value
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {error ? (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </div>
            ) : null}
            <Button className="w-fit" disabled={!name || !slug} onClick={() => void create()}>
              Create
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!creating && error ? (
        <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
        {workspaces.map((w) => (
          <Card key={w.id} className={w.id === workspaceId ? 'ring-2 ring-blue-600' : undefined}>
            <CardContent className="grid gap-3 pt-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <strong>{w.name}</strong>
                  <div className="text-xs text-gray-500">{w.slug}</div>
                </div>
                <Badge variant={statusVariant(w.status)}>{w.status}</Badge>
              </div>
              <div className="text-xs text-gray-500">mode: {w.defaultExecutionMode}</div>
              <div className="flex flex-wrap gap-2">
                {w.id !== workspaceId ? (
                  <Button size="sm" variant="outline" onClick={() => setWorkspace(w.id)}>
                    Use
                  </Button>
                ) : (
                  <Badge>active</Badge>
                )}
                {nextStatuses(w.status).map((status) => (
                  <Button key={status} size="sm" variant="ghost" onClick={() => void setStatus(w, status)}>
                    {actionLabel(status)}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {workspaces.length === 0 ? (
          <p className="text-sm text-gray-500">No workspaces yet. Create one to get started.</p>
        ) : null}
      </div>
    </div>
  );
}
